import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-maintenance-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './maintenance-page.component.html',
  styleUrls:   ['./maintenance-page.component.css']
})
export class MaintenancePageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() window: any = null;

  // Progress bar (0–100)
  progressPercent = 0;

  // Countdown display values
  countdownHours   = 0;
  countdownMinutes = 0;
  countdownSeconds = 0;

  // Elapsed display (for "time elapsed" row)
  elapsedText = '';

  // Derived once from startTime / endTime and never recalculated
  private startMs = 0;
  private endMs   = 0;

  private ticker: any; // 1-second interval

  ngOnInit(): void {
    this.init();
  }

  /**
   * ngOnChanges fires when the parent updates [window] after the first
   * active-maintenance poll — this is what makes the page populate correctly
   * even if @Input arrives after ngOnInit.
   */
  ngOnChanges(ch: SimpleChanges): void {
    if (ch['window'] && this.window) {
      this.init();
    }
  }

  ngOnDestroy(): void {
    this.stopTicker();
  }

  // ─────────────────────────────────────────────────────────────────
  // Initialise from the current [window] input.
  // Parses startTime / endTime ONCE so they never drift.
  // ─────────────────────────────────────────────────────────────────
  private init(): void {
    if (!this.window) return;

    const rawStart = this.window.startTime ?? this.window.start_time;
    const rawEnd   = this.window.endTime   ?? this.window.end_time  ?? this.window.estimatedEndTime;

    this.endMs   = rawEnd   ? new Date(rawEnd).getTime()   : 0;
    this.startMs = rawStart ? new Date(rawStart).getTime() : 0;

    // If startTime is missing or is in the future (shouldn't happen), fall back
    if (!this.startMs || this.startMs > Date.now()) {
      this.startMs = this.endMs - 4 * 60 * 60 * 1000; // assume 4-hour window
    }

    this.stopTicker();
    this.tick(); // immediate first render
    this.ticker = setInterval(() => this.tick(), 1000);
  }

  // ─────────────────────────────────────────────────────────────────
  // Called every second to recalculate all live values.
  // Uses the fixed startMs / endMs — never re-parses dates.
  // ─────────────────────────────────────────────────────────────────
  private tick(): void {
    const now     = Date.now();
    const total   = this.endMs   - this.startMs;
    const elapsed = now          - this.startMs;
    const remaining = this.endMs - now;

    // Progress: clamp 0–100
    this.progressPercent = total > 0
      ? Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)))
      : 0;

    // Countdown to end (hours : minutes : seconds)
    if (remaining > 0) {
      const remSec = Math.floor(remaining / 1000);
      this.countdownHours   = Math.floor(remSec / 3600);
      this.countdownMinutes = Math.floor((remSec % 3600) / 60);
      this.countdownSeconds = remSec % 60;
    } else {
      this.countdownHours   = 0;
      this.countdownMinutes = 0;
      this.countdownSeconds = 0;
    }

    // Elapsed text
    const elSec = Math.max(0, Math.floor(elapsed / 1000));
    const elH   = Math.floor(elSec / 3600);
    const elM   = Math.floor((elSec % 3600) / 60);
    this.elapsedText = elH > 0 ? `${elH}h ${elM}m elapsed` : `${elM}m elapsed`;
  }

  private stopTicker(): void {
    if (this.ticker) { clearInterval(this.ticker); this.ticker = null; }
  }

  // ─────────────────────────────────────────────────────────────────
  // Template helpers
  // ─────────────────────────────────────────────────────────────────

  /** "01 April 2026 | 12:00 AM – 4:00 AM" */
  get windowDateRange(): string {
    if (!this.startMs || !this.endMs) return '';
    const s = new Date(this.startMs);
    const e = new Date(this.endMs);
    const dateOpts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
    const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    return `${s.toLocaleDateString('en-IN', dateOpts)} | ${s.toLocaleTimeString('en-IN', timeOpts)} – ${e.toLocaleTimeString('en-IN', timeOpts)}`;
  }

  get impactNote(): string {
    return this.window?.notesImpact ?? this.window?.notes_impact ?? this.window?.message ?? this.window?.description ?? '';
  }

  get notifyRoles(): string[] {
    return this.window?.notifyRoles ?? this.window?.notify_roles ?? [];
  }

  /** Pad a number to 2 digits, e.g. 4 → "04" */
  pad(n: number): string {
    return n.toString().padStart(2, '0');
  }

  get isExpired(): boolean {
    return Date.now() >= this.endMs;
  }
}